import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart } from "../../../models/chart_model";
import { HarayanaDiscomData } from "../../../models/rajasthan";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { getMonthFromDate } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import { FilterTab } from "../../../components/Filters";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import { formatDateForAPI } from "../const";
import { ChhattisgarhPeakAnalysis } from "./DiscomPeak";
import { ChhattisgarhSummaryAnalysis } from "./DiscomSummary";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
  import {
    getChhattisgarhDiscomData,
    getDiscomLoadMetrics,
    getDPROther,
  } from "../Utility/RestApi";
  import { DiscomLoadMetrics } from "../../../models/metrics";
  import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
  import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
  import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
  import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
  import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
  let Intervals = [
    {
      label: "Time Slot",
      key: "slot",
      values: ["block", "date"],
      isTime: true,
    },
    {
      label: "Date",
      key: "date",
      values: ["date"],
      isTime: false,
    },
    {
      label: "Month",
      key: "month",
      values: ["month"],
      isTime: false,
    },
  ];

  const X_AXIS_MODES = [
    { label: "Intervals", value: "intervals" },
    { label: "Peak Analysis", value: "peak" },
    { label: "Summary", value: "summary" },
  ];

  const UNITS = ["MW", "MWh", "LU"];

  const GROUP_BY_OPTIONS = [
    "Discom Load",
    "Total Discom Load",
    "CSPDCL Drawal",
  ];
  const DISCOM_OPTIONS = ["CSPDCL"];
  const TYPE_OPTIONS = [
    "Actual Load",
    "Total Availability",
    "OD/UD",
    "Inhand Availability",
    "Total Injection",
    "CS Schedule",
    "Unscheduled Load Shedding",
  ];

  // Helper function to get month with year (e.g., "Oct-24" instead of just "Oct")
  export function getMonthWithYear(dateString: string): string {
    const date = new Date(dateString);
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
    return `${month}-${year}`;
  }

  export function ChattisgarhDiscomAnalysis() {
    const dataTab = useSelector((state: any) => state.viewMode.activeView);
    const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
    const DefaultDates = useSelector(
      (state: any) => state.defaultDatesSetter.defaultDates,
    );

    const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);

    const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
    const [data, setData] = useState<HarayanaDiscomData[]>([]);
    const [chartKeys, setChartKeys] = useState<any[]>([]);
    const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
    const [selectedArea, setSelectedArea] = useState<any[]>([]);
    const [uniqueDate, setUniqueDate] = useState<any[]>([]);
    const [selectedDate, setSelectedDate] = useState<any[]>([]);
    const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
    const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
    const [chartData, setChartData] = useState<any[]>([]);
    const [TableData, setTableData] = useState<any[]>([]);
    const [selectedChartType, setSelectedChartType] = useState(
      H_CHART_TYPES[1],
    );
    const [selectedGroupKeys, setSelectedGroupKeys] = useState(
      GROUP_BY_OPTIONS[0],
    );
    const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState<DiscomLoadMetrics | null>(null);
    const [metricsLoading, setMetricsLoading] = useState(false);

    const chartSuffixes = [
      { suffix: "demand", displayName: "Actual Load", stackSuffix: "demand" },
      {
        suffix: "total_availability",
        displayName: "Total Availability",
        stackSuffix: "total_availability",
      },
      { suffix: "od_ud", displayName: "OD/UD", stackSuffix: "od_ud" },
      {
        suffix: "inhand_availability",
        displayName: "Inhand Availability",
        stackSuffix: "inhand_availability",
      },
      {
        suffix: "total_injection",
        displayName: "Total Injection",
        stackSuffix: "total_injection",
      },
      {
        suffix: "cs_schedule",
        displayName: "CS Schedule",
        stackSuffix: "cs_schedule",
      },
      {
        suffix: "unscheduled_load_shedding",
        displayName: "Unscheduled Load Shedding",
        stackSuffix: "unscheduled_load_shedding",
      },
    ];

    const createChartObject = (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      selectedInterval: { isTime: boolean },
      selectedChartType: { value: string; stackId?: string },
      ExchangeColors: string[],
    ) => {
      return {
        dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
        name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
        legendColor: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? stackSuffix : undefined,
      };
    };

    const [selectedSuffix, setSelectedSuffix] = useState([
      chartSuffixes[0], // Actual Load
      chartSuffixes[1], // Total Availability
    ]);

    const _handlefetchData = useCallback(async () => {
      if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
      setLoading(true);
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      try {
        const [data, otherData] = await Promise.all([
          getChhattisgarhDiscomData(startDateStr, endDateStr),
          getDPROther(startDateStr, endDateStr),
        ]);

        const otherDataMap = new Map();
        otherData.forEach((item: any) => {
          const key = `${item.date}_${item.slot || item.block}`;
          otherDataMap.set(key, item);
        });

        const newData = data.map((item: any) => {
          const key = `${item.date}_${item.block}`;
          const other = otherDataMap.get(key) || {};
          return {
            ...item,
            total_availability_mw: other.total_availability || 0,
            total_availability_mwh: (other.total_availability || 0) / 4,
            total_availability_lu: (other.total_availability || 0) / 400,
            od_ud_mw: other.od_ud || 0,
            od_ud_mwh: (other.od_ud || 0) / 4,
            od_ud_lu: (other.od_ud || 0) / 400,
            inhand_availability_mw: other.inhand_availability || 0,
            inhand_availability_mwh: (other.inhand_availability || 0) / 4,
            inhand_availability_lu: (other.inhand_availability || 0) / 400,
            total_injection_mw: other.total_injection || 0,
            total_injection_mwh: (other.total_injection || 0) / 4,
            total_injection_lu: (other.total_injection || 0) / 400,
            cs_schedule_mw: other.cs_schedule || 0,
            cs_schedule_mwh: (other.cs_schedule || 0) / 4,
            cs_schedule_lu: (other.cs_schedule || 0) / 400,
            unscheduled_load_shedding_mw: other.unscheduled_load_shedding || 0,
            unscheduled_load_shedding_mwh:
              (other.unscheduled_load_shedding || 0) / 4,
            unscheduled_load_shedding_lu:
              (other.unscheduled_load_shedding || 0) / 400,
          };
        });

        setData(newData);

        setUniqueDate(
          Array.from(
            new Set(
              newData
                .map((item) => item.date)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
            ),
          ),
        );
        setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
      } catch (error) {
        console.error("Error in data fetching:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    }, [DefaultDates, activeTab]);
    const fetchMetrics = useCallback(async () => {
      if (!DefaultDates[activeTab]?.defaultRangeDate) {
        setMetrics(null);
        return;
      }

      setMetricsLoading(true);
      try {
        const dates = DefaultDates[activeTab].defaultRangeDate;
        const startDateStr = formatDateForAPI(dates.startDate);
        const endDateStr = formatDateForAPI(dates.endDate);

        const metricsData = await getDiscomLoadMetrics({
          ranges: [
            {
              startDate: startDateStr,
              endDate: endDateStr,
              blocks: selectedBlock.map((block) => block.value),
            },
          ],
          aggregated: true,
        });

        setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetrics(null);
      } finally {
        setMetricsLoading(false);
      }
    }, [DefaultDates, activeTab, selectedBlock]);

    useEffect(() => {
      fetchMetrics();
    }, [fetchMetrics]);

    useEffect(() => {
      // FIXED: Use getMonthWithYear instead of getMonthFromDate to include year
      let filteredData = data.map((item) => ({
        ...item,
        month: getMonthWithYear(item.date), // This now returns "Oct-24" instead of just "Oct"
      }));

      if (selectedDate.length > 0) {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((item) => item.value).includes(item.date),
        );
      }
      if (selectedBlock.length > 0) {
        filteredData = filteredData.filter((item) =>
          selectedBlock.map((item) => item.value).includes(item.block),
        );
      }

      // FIXED: Sort filtered data in ascending order by date and block
      filteredData.sort((a, b) => {
        // First sort by date
        const dateCompare =
          new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateCompare !== 0) return dateCompare;

        // Then sort by block if dates are the same
        return a.block - b.block;
      });

      function getKeys() {
        if (selectedInterval.key === "slot") {
          return ["date", "block"];
        } else if (selectedInterval.key === "date") {
          return ["date"];
        } else {
          return [selectedInterval.values[0]];
        }
      }

      const par: ChartDataParams = {
        keys: getKeys(),
        groupBy: [],
        tableKeys: ["date", "block", "month"],
        volumeKey: ``,
        aggregationKeys: [
          "demand_mw",
          "demand_mwh",
          "demand_lu",
          "total_availability_mw",
          "total_availability_mwh",
          "total_availability_lu",
          "od_ud_mw",
          "od_ud_mwh",
          "od_ud_lu",
          "inhand_availability_mw",
          "inhand_availability_mwh",
          "inhand_availability_lu",
          "total_injection_mw",
          "total_injection_mwh",
          "total_injection_lu",
          "cs_schedule_mw",
          "cs_schedule_mwh",
          "cs_schedule_lu",
          "unscheduled_load_shedding_mw",
          "unscheduled_load_shedding_mwh",
          "unscheduled_load_shedding_lu",
        ],
        priceKey: "",
        averageKeys: [
          "demand_mw",
          "demand_mwh",
          "demand_lu",
          "total_availability_mw",
          "total_availability_mwh",
          "total_availability_lu",
          "od_ud_mw",
          "od_ud_mwh",
          "od_ud_lu",
          "inhand_availability_mw",
          "inhand_availability_mwh",
          "inhand_availability_lu",
          "total_injection_mw",
          "total_injection_mwh",
          "total_injection_lu",
          "cs_schedule_mw",
          "cs_schedule_mwh",
          "cs_schedule_lu",
          "unscheduled_load_shedding_mw",
          "unscheduled_load_shedding_mwh",
          "unscheduled_load_shedding_lu",
        ],
        averageDuplicationKeys: ["date", "block", "month"],
        data: filteredData,
      };
      const cData = FormatChartData(par);
      setChartData(cData.data);
      setChartKeys(cData.keys);
      setTableData(cData.tableData || []);
    }, [
      data,
      selectedGroupKeys,
      selectedInterval,
      selectedDiscom,
      selectedArea,
      selectedDate,
      selectedBlock,
      selectedSuffix,
      selectedUnit,
    ]);

    useEffect(() => {
      _handlefetchData();
    }, [_handlefetchData]);

    function handleUnitChange(params: string) {
      setSelectedUnit(params);
    }

    function handleXAxisModeChange(item: string, index: number) {
      setSelectedXAxisMode(X_AXIS_MODES[index]);
    }

    function handleGroupByChange(index: number) {
      setSelectedGroupKeys(GROUP_BY_OPTIONS[index]);
    }

    function handleDiscomChange(items: string[]) {
      setSelectedDiscom(items);
    }

    function handleTypeChange(selected: string[]) {
      const newSuffix = selected
        .map((s) => chartSuffixes.find((cs) => cs.displayName === s))
        .filter(Boolean) as typeof chartSuffixes;
      setSelectedSuffix(newSuffix.length > 0 ? newSuffix : [chartSuffixes[0]]);
    }

    const handlexDataKey = useCallback(() => {
      if (selectedInterval.isTime) {
        if (selectedBlock.length > 0) {
          if (selectedInterval.isTime) {
            return "block";
          } else {
            return selectedInterval.values[0];
          }
        }
        return "block";
      } else {
        return selectedInterval.key;
      }
    }, [
      selectedBlock.length,
      selectedInterval.isTime,
      selectedInterval.key,
      selectedInterval.values,
    ]);

    const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

    function secondDataKey() {
      if (selectedInterval.isTime) {
        if (selectedBlock.length > 0) {
          return "date";
        }
        return "date";
      } else {
        return undefined;
      }
    }

    const summaryExtraKeys = selectedInterval.isTime
      ? ["block", selectedInterval.values[1]]
      : [selectedInterval.values[0]];

    const summaryExtraLabels = selectedInterval.values.map((value) =>
      value === "date" ? "Date" : value,
    );

    const { visibleLegends, handleLegendSelect, selectedLegends } =
      useLegendFilter(getLegends());

    const tablePar = {
      legends: visibleLegends,
      data: chartData,
      tableData: TableData,
      itemsPerPage: 96,
      block_key: "block",
      unit: selectedUnit,
      extraKeys: summaryExtraKeys,
      extraLabels: summaryExtraLabels,
      filename: AddDatesWithFileName({
        fileName: "CSPDCL Load",
        dates: DefaultDates[activeTab]?.defaultRangeDate || [],
      }),
    };

    if (selectedXAxisMode.value === "peak") {
      return (
        <ChhattisgarhPeakAnalysis
          onXAxisModeChange={handleXAxisModeChange}
          selectedUnit={selectedUnit}
          onUnitChange={handleUnitChange}
          selectedDiscom={selectedDiscom}
          onDiscomChange={handleDiscomChange}
          selectedGroupKeys={selectedGroupKeys}
          onGroupByChange={handleGroupByChange}
          selectedSuffix={selectedSuffix}
          onTypeChange={handleTypeChange}
          chartSuffixes={chartSuffixes}
          metrics={metrics}
        />
      );
    }

    if (selectedXAxisMode.value === "summary") {
      return (
        <ChhattisgarhSummaryAnalysis
          onXAxisModeChange={handleXAxisModeChange}
          selectedUnit={selectedUnit}
          onUnitChange={handleUnitChange}
          selectedDiscom={selectedDiscom}
          onDiscomChange={handleDiscomChange}
          selectedGroupKeys={selectedGroupKeys}
          onGroupByChange={handleGroupByChange}
          selectedSuffix={selectedSuffix}
          onTypeChange={handleTypeChange}
          chartSuffixes={chartSuffixes}
          metrics={metrics}
        />
      );
    }

    return (
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="X-Axis"
              items={X_AXIS_MODES.map((item) => item.label)}
              selectedItems={[selectedXAxisMode.label]}
              onSelect={handleXAxisModeChange}
            />

            <FilterMenu
              title="Intervals"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
              }}
            />

            <FilterMenu
              title="Unit"
              items={UNITS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                handleUnitChange(item);
              }}
            />
            {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={handleDiscomChange}
            // disabled={true}
          /> */}
            <FilterMenu
              title="Type"
              items={TYPE_OPTIONS}
              selectedItems={selectedSuffix.map((s) => s.displayName)}
              setSelectedItems={handleTypeChange}
              // disabled={true}
            />
            <FilterMenu
              title="Chart Type"
              items={H_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) => {
                setSelectedChartType(H_CHART_TYPES[index]);
              }}
            />

            {/* <FilterMenu
            title="Group By"
            items={GROUP_BY_OPTIONS}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => handleGroupByChange(index)}
            disabled={true}
          /> */}
          </div>
        </NewFilterContainer>
        <MainPageContainer>
          <SecondaryHeader />
          {metrics && (
            <div className="flex flex-wrap gap-4 my-0 justify-center">
              <MetricChip
                icon={<PeakIcon width={20} height={19} />}
                items={[
                  {
                    label: "Peak Load",
                    value: metrics["Peak Load (MW)"].value,
                    unit: metrics["Peak Load (MW)"].unit,
                  },
                ]}
              />
              <MetricChip
                icon={<AvgIcon width={20} height={19} />}
                items={[
                  {
                    label: "Average Load",
                    value: metrics["Average Load (MW)"].value,
                    unit: metrics["Average Load (MW)"].unit,
                  },
                ]}
              />
              <MetricChip
                icon={<MinIcon width={20} height={19} />}
                items={[
                  {
                    label: "Minimum Load",
                    value: metrics["Minimum Load (MW)"].value,
                    unit: metrics["Minimum Load (MW)"].unit,
                  },
                ]}
              />
              <MetricChip
                icon={<TotalIcon width={20} height={19} />}
                items={[
                  {
                    label: "Total Energy",
                    value: metrics["Total Energy (MU)"].value,
                    unit: metrics["Total Energy (MU)"].unit,
                  },
                ]}
              />
            </div>
          )}
          <div className="gas-chart-container">
            {loading ? (
              <ChartAreaLoader
                title="Loading Discom Load"
                subtitle="Fetching Chhattisgarh discom drawal and schedule data…"
              />
            ) : (
              <>
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">
                    Discom Load ({selectedUnit})
                  </h2>
                  <div className="flex gap-x-6  justify-end">
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                    <button
                      onClick={() => {
                        dataTab.label === "Chart" &&
                          downloadChartTableCSV(tablePar);
                        dataTab.label === "Summary" &&
                          downloadSummaryCSV(tablePar);
                        dataTab.label === "Table" &&
                          downloadChartTableCSV(tablePar);
                      }}
                      className="downloadButton"
                    >
                      Download Data
                    </button>
                  </div>
                </div>

                {dataTab.label === "Chart" && (
                  <ReMixChart
                    data={chartData}
                    showBrush={chartData.length > 30 ? true : undefined}
                    brushStart={BrushStart.Start}
                    isTimeSlot={selectedInterval.isTime}
                    onlyTitle={true}
                    barCategoryGap={10}
                    unit={selectedUnit}
                    yAxisLabel={selectedUnit}
                    legends={getLegends()}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    xDataKey={xDataKey}
                    xTick={
                      selectedInterval.isTime && selectedBlock.length === 0
                        ? (value: any, index: any) => {
                            return renderHourTick(value, 10);
                          }
                        : undefined
                    }
                    secondXDataKey={secondDataKey()}
                  />
                )}
                {dataTab.label === "Summary" && (
                  <LegendSummaryTable {...tablePar} />
                )}
                {dataTab.label === "Table" && <ChartTable {...tablePar} />}
              </>
            )}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={<p>CSPDCL</p>}
                text=" 
            1. Actual Load data is sourced from CSPDCL's official records."
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    );

    function getLegends() {
      return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              selectedInterval,
              selectedChartType as any,
              ExchangeColors,
            ) as any,
        ),
      );
    }

  }
